import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Collapse } from 'reactstrap';

// Helpers
import { isEmptyString, validEmail } from 'Shared/helpers';

// Types
import { WorkspaceType } from 'Shared/constants/types';

// Utils
import { useAddTeammate } from 'Shared/utils/hooks/use-add-teammate';
import { useCopyToClipboard } from 'Shared/utils/hooks/use-copy-to-clipboard';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useMembers } from 'Shared/utils/hooks';

// Selectors
import { getMembersWhoHaveNotTeammateYet } from 'Shared/selectors/members';

// Components
import Select from './Select/Select';
import SimpleAddWorkspaceWrapper from '../SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import { Illustrations } from 'Wpds/components/illustration';
import TextField from 'Shared/components/TextField/TextField';

// Styles
import './SimpleAddTeammateModal.scss';
import { WpIcon } from 'Wpds/components/icon';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
type Props = {
  onClose: Function;
};
function SimpleAddTeammateModal({ onClose }: Props) {
  const { user } = useCurrentUser();
  const inviteLink = user.inviteLink;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading: isLoadingMembers } = useMembers();

  const members = useSelector(getMembersWhoHaveNotTeammateYet);
  const { tr } = useTranslation();
  const emailValidation = value => {
    if (value && !validEmail(value)) return tr('perform.workspace.simple_add_teammate.invalid_email', value);
    return undefined;
  };

  const { formValues, onTextFieldsChange, disabledSubmit, isInvalidEmail, onSubmit, setFormValues } = useAddTeammate({
    isSubmitting,
    setIsSubmitting,
  });
  const emailOptions = members?.map(({ id, fullName, email }) => ({ id, email, fullName }));

  const { onCopyTextToClipboard } = useCopyToClipboard({ textToCopy: inviteLink });

  const copyInviteLink = () => {
    (window as any).analytics.track('Copied Invite Link', { is_group: false });
    onCopyTextToClipboard();
  };

  const handleSelectChange = useCallback(
    value => {
      (window as any).analytics.track('Clicked in Add Workspace Modal', {
        modal: 'add_teammate',
        button: 'Meeting',
      });

      setFormValues({ ...formValues, email: value });
    },
    [formValues, setFormValues]
  );

  const isShowPersonalMessage = !isInvalidEmail() && !isEmptyString(formValues.email);

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isTeammate}
      title={tr('perform.workspace.simple_add_teammate.title')}
      description={tr('perform.workspace.simple_add_teammate.description')}
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      createNameSection={
        <div className="d-flex justify-content-between flex-column flex-sm-row align-items-end mt-3">
          <div className="w-100 mr-0 mr-sm-2">
            <TextField
              name="firstName"
              type="text"
              label={tr('perform.workspace.simple_add_teammate.first_name_label')}
              labelMuted={false}
              placeholder={tr('perform.workspace.simple_add_teammate.first_name_placeholder')}
              value={formValues.firstName}
              onChange={onTextFieldsChange}
            />
          </div>
          <TextField
            name="lastName"
            type="text"
            labelMuted={false}
            label={tr('perform.workspace.simple_add_teammate.last_name_label')}
            placeholder={tr('perform.workspace.simple_add_teammate.last_name_placeholder')}
            value={formValues.lastName}
            onChange={onTextFieldsChange}
          />
        </div>
      }
      optionToInviteSection={
        <form autoComplete="off">
          <Select
            value={formValues.email}
            options={emailOptions}
            onChange={handleSelectChange}
            validate={emailValidation}
            isLoading={isLoadingMembers}
          />
          <Collapse isOpen={isShowPersonalMessage}>
            {isShowPersonalMessage ? (
              <div className="d-flex flex-column">
                <div className="small font-weight-bold text-muted mb-1">
                  {tr('perform.workspace.simple_add_teammate.personal_message_optional')}
                </div>
                <textarea
                  name="personalMessage"
                  className="w-100 border rounded p-2"
                  value={formValues.personalMessage}
                  onChange={onTextFieldsChange}
                />
              </div>
            ) : null}
          </Collapse>
          <div>
            {tr('perform.common.or')}
            <button type="button" className="btn btn-link px-1" onClick={copyInviteLink}>
              <WpIcon name="link" /> {tr('perform.workspace.simple_add_teammate.copy_invite_link')}
            </button>
          </div>
          <div className="simple-add-teammate-modal-description d-none d-sm-block">
            {tr('perform.workspace.simple_add_teammate.domain_disclaimer')}
          </div>
        </form>
      }
      illustration={Illustrations.two_people_standing_with_laptop_purple_bg}
    />
  );
}

export default SimpleAddTeammateModal;
